import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { LoginModal } from "./LoginModal";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export function FullScreenHero() {
  const {
    user,
    userProfile,
    signInWithGoogle,
    setShowLoginModal,
    showLoginModal,
    handleLoginModalSubmit,
    loading,
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only redirect after loading is complete AND we have user info
    if (!user || loading || isLoading) {
      console.log(
        "[FULLSCREEN-HERO] Not ready to redirect - user:",
        !!user,
        "loading:",
        loading,
        "isLoading:",
        isLoading,
      );
      return;
    }

    console.log("[FULLSCREEN-HERO] Checking redirect conditions");
    console.log("[FULLSCREEN-HERO] User:", user.email, "Role:", user.role);
    console.log(
      "[FULLSCREEN-HERO] UserProfile:",
      userProfile?.profile_completed,
    );

    // If admin, redirect to admin panel after profile completion
    if (user.role === "admin") {
      if (userProfile?.profile_completed) {
        console.log(
          "[FULLSCREEN-HERO] Admin with profile, redirecting to /admin",
        );
        navigate("/admin", { replace: true });
      } else {
        console.log(
          "[FULLSCREEN-HERO] Admin without profile, redirecting to /profile-setup",
        );
        navigate("/profile-setup", { replace: true });
      }
    } else {
      // Regular user flow
      if (userProfile?.profile_completed) {
        console.log(
          "[FULLSCREEN-HERO] Regular user with profile, redirecting to /dashboard",
        );
        navigate("/dashboard", { replace: true });
      } else {
        console.log(
          "[FULLSCREEN-HERO] Regular user without profile, redirecting to /profile-setup",
        );
        navigate("/profile-setup", { replace: true });
      }
    }
  }, [user, userProfile, loading, navigate, isLoading]);

  const handleLogin = () => {
    console.log("[FULLSCREEN-HERO] Opening login modal");
    signInWithGoogle();
  };

  const handleLoginSubmit = async (credentials) => {
    try {
      setIsLoading(true);
      await handleLoginModalSubmit(credentials);
      setShowLoginModal(false);
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
    }
  };

  const scrollToFeatures = () => {
    const element = document.getElementById("features");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <LoginModal
        isOpen={showLoginModal}
        onLogin={handleLoginSubmit}
        onClose={() => setShowLoginModal(false)}
      />

      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Background GIF */}
        <img
          src="/herobggif.gif"
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-vitapink-100/80 via-white/70 to-gray-50/80"></div>

        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

        {/* Launch Badge */}
        <Badge
          variant="secondary"
          className="absolute top-24 right-8 bg-vitapink text-white hover:bg-vitapink-700 px-4 py-2 text-sm font-bold"
        >
          🚀 Lançamento SaaS
        </Badge>

        {/* Main Content */}
        <div className="relative z-30 max-w-4xl mx-auto px-6 text-center">
          {/* Date Badge */}
          <Badge
            variant="outline"
            className="mb-8 text-base px-6 py-3 border-2 border-vitapink text-vitapink font-semibold"
          >
            📅 01/03/2026 | Lançamento Oficial
          </Badge>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            O Futuro do Seu Negócio{" "}
            <span className="text-vitapink">Começa Aqui</span>
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-3xl mx-auto">
            Bem-vindos à VitaCode
          </p>
          <p className="text-lg md:text-xl text-gray-500 mb-12 max-w-2xl mx-auto">
            Trazendo o código da sua vida através de desenvolvimento, gestão de
            mídias e sistemas inteligentes
          </p>

          {/* CTA Button */}
          <Button
            size="lg"
            className="bg-vitapink hover:bg-vitapink-700 text-white px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            onClick={handleLogin}
            disabled={isLoading}
          >
            {isLoading
              ? "Carregando..."
              : "Clique Aqui e Faça Seu Pré-Registro →"}
          </Button>

          {/* Small text */}
          <p className="mt-6 text-sm text-gray-400">
            Lançamento do nosso SaaS de gestão financeira
          </p>
        </div>

        {/* Scroll Hint */}
        <button
          onClick={scrollToFeatures}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-400 hover:text-vitapink transition-colors cursor-pointer z-40"
        >
          <span className="text-sm font-medium">Role para explorar</span>
          <ArrowDown className="w-6 h-6 animate-bounce" />
        </button>
      </section>
    </>
  );
}
